(function () {
  'use strict';

  const Engine = window.Lumira3DEngine;
  if (!Engine) return;

  const AUTO_PREVIEW_MS = 3200;
  const CAMERA_FIT = {
    azimuth: '0deg',
    polar: '75deg',
    fovDegrees: 45,
    padding: 1.2,
    minFactor: 0.4,
    maxFactor: 2.5,
    zoomStep: 0.15
  };
  const LOADING_TIPS = [
    'Tip: rotate slowly to inspect glass edges and joinery.',
    'Tip: switch lighting to preview the finish in warm and cool rooms.',
    'Tip: use AR on your phone to check scale before ordering.',
    'Tip: compare mode accepts another Lumira product URL or a direct GLB link.'
  ];

  function moneyLabel(config, variant) {
    return (variant && variant.price) || config.product.price || '';
  }

  function setPressed(buttons, value) {
    buttons.forEach((button) => {
      const active = button.dataset.value === value;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
  }

  function setText(node, value) {
    if (node) node.textContent = value;
  }

  function normalize(value) {
    return String(value || '').toLowerCase();
  }

  function parseDimensions(value) {
    const text = normalize(value);
    const out = { width: 0, depth: 0, height: 0 };
    [
      ['width', /(?:width|w)\s*:?\s*(\d+(?:\.\d+)?)/],
      ['depth', /(?:depth|d)\s*:?\s*(\d+(?:\.\d+)?)/],
      ['height', /(?:height|h)\s*:?\s*(\d+(?:\.\d+)?)/]
    ].forEach(([key, pattern]) => {
      const match = text.match(pattern);
      if (match) out[key] = Number(match[1]);
    });
    if (!out.width || !out.depth || !out.height) {
      const nums = text.match(/\d+(?:\.\d+)?/g) || [];
      if (nums.length >= 3) {
        out.width = out.width || Number(nums[0]);
        out.depth = out.depth || Number(nums[1]);
        out.height = out.height || Number(nums[2]);
      }
    }
    return out;
  }

  function inferKind(config) {
    const text = normalize([config.product.title, config.product.dimensions].join(' '));
    if (text.includes('nightstand')) return 'nightstand';
    if (text.includes('sectional')) return 'sectional';
    if (text.includes('loveseat')) return 'loveseat';
    if (text.includes('sofa')) return 'sofa';
    if (text.includes('coffee')) return 'coffee table';
    if (text.includes('dining')) return 'dining';
    if (text.includes('bed')) return 'bed';
    return 'furniture';
  }

  function fallbackDimensions(kind) {
    if (kind === 'sectional') return { width: 112, depth: 72, height: 34 };
    if (kind === 'loveseat') return { width: 60, depth: 36, height: 36 };
    if (kind === 'sofa') return { width: 84, depth: 38, height: 36 };
    if (kind === 'coffee table') return { width: 48, depth: 26, height: 18 };
    if (kind === 'bed') return { width: 80, depth: 84, height: 48 };
    return { width: 36, depth: 30, height: 30 };
  }

  function fitGuidance(config, remoteCheck) {
    const kind = inferKind(config);
    const parsed = parseDimensions(config.product.dimensions);
    if (kind === 'nightstand') {
      return [
        `Cabinet footprint: ${parsed.width || '?'} × ${parsed.depth || '?'} inches.`,
        'Allow room in front for the drawers to open fully.',
        'Keep the adjacent walking route clear.',
        'Confirm the top height against your mattress before ordering.'
      ];
    }
    const fallback = fallbackDimensions(kind);
    const dims = {
      width: parsed.width || fallback.width,
      depth: parsed.depth || fallback.depth,
      height: parsed.height || fallback.height
    };
    const roomW = Math.max(10, Math.ceil((dims.width + 60) / 12));
    const roomD = Math.max(10, Math.ceil((dims.depth + (kind === 'sectional' || kind === 'sofa' ? 96 : 60)) / 12));
    const rug = dims.width >= 96 || kind === 'sectional' ? '9x12' : dims.width >= 72 ? '8x10' : '5x8';
    const guidance = [
      `Fits rooms ${roomW}x${roomD} ft or larger.`,
      `Recommended rug size: ${rug}.`,
      kind === 'coffee table' ? 'Coffee table to seating: 14-18 inches.' : 'Coffee table to seating: 14-18 inches when paired.',
      'Walking path needed: 30 inches minimum.'
    ];
    if (remoteCheck && remoteCheck.suggestions && remoteCheck.suggestions[0]) guidance.push(remoteCheck.suggestions[0]);
    return guidance;
  }

  function targetWidthMeters(config) {
    const kind = inferKind(config);
    const parsed = parseDimensions(config.product.dimensions);
    const fallback = fallbackDimensions(kind);
    return ((parsed.width || fallback.width) * 0.0254);
  }

  function measurePhysicalScale(viewer, config) {
    if (!viewer || typeof viewer.getDimensions !== 'function') return 1;
    try {
      const dimensions = viewer.getDimensions();
      const currentWidth = Math.max(Number(dimensions.x) || 0, Number(dimensions.z) || 0);
      const targetWidth = targetWidthMeters(config);
      if (!currentWidth || !targetWidth) return 1;
      const factor = Math.max(0.1, Math.min(12, targetWidth / currentWidth));
      return factor;
    } catch (error) {
      console.warn('[Lumira 3D] Could not measure physical scale', error);
      return 1;
    }
  }

  function parseCameraDistance(value) {
    const parts = String(value || '').trim().split(/\s+/);
    const radius = parts[2] || '';
    const distance = parseFloat(radius);
    return Number.isFinite(distance) ? distance : 0;
  }

  function applyCameraLimits(viewer, fit) {
    viewer.setAttribute('field-of-view', `${CAMERA_FIT.fovDegrees}deg`);
    viewer.setAttribute('interpolation-decay', '90');
    viewer.setAttribute('min-camera-orbit', `auto auto ${fit.minDistance.toFixed(4)}m`);
    viewer.setAttribute('max-camera-orbit', `auto auto ${fit.maxDistance.toFixed(4)}m`);
    viewer.setAttribute('camera-target', 'auto auto auto');
  }

  function setViewerDistance(viewer, fit, distance, azimuth, polar) {
    const clamped = Math.max(fit.minDistance, Math.min(fit.maxDistance, distance));
    viewer.setAttribute('camera-orbit', `${azimuth || CAMERA_FIT.azimuth} ${polar || CAMERA_FIT.polar} ${clamped.toFixed(4)}m`);
    return clamped;
  }

  function currentCameraParts(viewer, fit) {
    const current = String(viewer.getAttribute('camera-orbit') || viewer.cameraOrbit || '');
    const parts = current.trim().split(/\s+/);
    return {
      azimuth: parts[0] || CAMERA_FIT.azimuth,
      polar: parts[1] || CAMERA_FIT.polar,
      distance: parseCameraDistance(current) || (fit && fit.distance) || 1
    };
  }

  function computeViewerFit(viewer) {
    if (!viewer || typeof viewer.getDimensions !== 'function') return null;
    try {
      const dimensions = viewer.getDimensions();
      // Fit the complete bounding sphere, not half the longest axis. The old
      // fit could put the near cabinet face outside the camera's frustum.
      const radius = Math.hypot(Number(dimensions.x) || 0,
        Number(dimensions.y) || 0, Number(dimensions.z) || 0) / 2;
      if (!radius) return null;
      const fovRadians = CAMERA_FIT.fovDegrees * Math.PI / 180;
      const rect = viewer.getBoundingClientRect();
      const aspect = rect.width > 0 && rect.height > 0 ? rect.width / rect.height : 1;
      const halfFov = Math.min(fovRadians / 2,
        Math.atan(Math.tan(fovRadians / 2) * aspect));
      const distance = (radius / Math.sin(halfFov)) * CAMERA_FIT.padding;
      return {
        distance,
        minDistance: distance * CAMERA_FIT.minFactor,
        maxDistance: distance * CAMERA_FIT.maxFactor
      };
    } catch (error) {
      console.warn('[Lumira 3D] Could not compute camera fit', error);
      return null;
    }
  }

  function fitViewerCamera(viewer, state) {
    const fit = computeViewerFit(viewer);
    if (!fit) return null;
    state.cameraFit = fit;
    applyCameraLimits(viewer, fit);
    setViewerDistance(viewer, fit, fit.distance, CAMERA_FIT.azimuth, CAMERA_FIT.polar);
    return fit;
  }

  function resetViewerCamera(root, state) {
    const viewer = root.querySelector('[data-lumira-model-viewer]');
    if (!viewer) return;
    const fit = state.cameraFit || fitViewerCamera(viewer, state);
    if (!fit) return;
    applyCameraLimits(viewer, fit);
    setViewerDistance(viewer, fit, fit.distance, CAMERA_FIT.azimuth, CAMERA_FIT.polar);
    if (typeof viewer.resetTurntableRotation === 'function') viewer.resetTurntableRotation();
  }

  function zoomViewer(root, state, direction, boost) {
    const viewer = root.querySelector('[data-lumira-model-viewer]');
    if (!viewer) return;
    const fit = state.cameraFit || fitViewerCamera(viewer, state);
    if (!fit) return;
    const current = currentCameraParts(viewer, fit);
    const step = CAMERA_FIT.zoomStep * Math.max(1, Number(boost) || 1);
    const nextDistance = direction === 'in'
      ? current.distance * Math.max(0.2, 1 - step)
      : current.distance * (1 + step);
    applyCameraLimits(viewer, fit);
    setViewerDistance(viewer, fit, nextDistance, current.azimuth, current.polar);
  }

  function renderFitGuidance(root, config, remoteCheck) {
    let slot = root.querySelector('[data-lumira-3d-fit]');
    if (!slot) {
      const dimensions = root.querySelector('.lumira3d-dimensions');
      const anchor = dimensions && dimensions.closest('.lumira3d-section');
      if (!anchor || !anchor.parentNode) return;
      const section = document.createElement('div');
      section.className = 'lumira3d-section';
      section.innerHTML = '<h3>Recommended Clearance</h3><div class="lumira3d-fit" data-lumira-3d-fit></div>';
      anchor.parentNode.insertBefore(section, anchor.nextSibling);
      slot = section.querySelector('[data-lumira-3d-fit]');
    }
    if (!slot) return;
    slot.innerHTML = `<ul>${fitGuidance(config, remoteCheck).map((item) => `<li>${item}</li>`).join('')}</ul>`;
  }

  function requestFitGuidance(root, config) {
    const endpoint = root.dataset.spatialEndpoint || 'https://n8n.tnmseo.com/webhook/sophi';
    if (!endpoint) return;
    const kind = inferKind(config);
    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'check_clearance',
        session_id: 'product-3d-fit',
        message: 'Generate product fit guidance.',
        item: {
          id: config.product.id,
          title: config.product.title,
          kind,
          dimensions: config.product.dimensions,
          position: { x: 0, y: 0, z: 0, scale: 1 }
        },
        scene: {
          room: { estimated_dimensions: { width_ft: 12, depth_ft: 14, height_ft: 9 } },
          products: [{
            id: config.product.id,
            title: config.product.title,
            kind,
            dimensions: config.product.dimensions,
            position: { x: 0, y: 0, z: 0, scale: 1 }
          }]
        },
        room_dimensions: { width_ft: 12, depth_ft: 14, height_ft: 9 }
      })
    }).then((response) => {
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    }).then((payload) => {
      const spatial = (payload.scene_advice && payload.scene_advice.spatial_intelligence) || payload;
      const check = payload.status ? payload : spatial && spatial.checks && spatial.checks[0];
      renderFitGuidance(root, config, check || null);
    }).catch(() => null);
  }

  function updateLoading(root, progress) {
    const normalized = Math.max(0, Math.min(1, Number(progress) || 0));
    const percent = Math.round(normalized * 100);
    const bar = root.querySelector('[data-lumira-3d-progress-bar]');
    const text = root.querySelector('[data-lumira-3d-progress-text]');
    if (bar) bar.style.setProperty('--lumira-progress', `${percent}%`);
    setText(text, percent > 0 ? `Loading model ${percent}%` : 'Loading model');
  }

  function startTipRotation(root, state) {
    const tip = root.querySelector('[data-lumira-3d-loading-tip]');
    if (!tip || state.tipTimer) return;
    let index = 0;
    setText(tip, LOADING_TIPS[index]);
    state.tipTimer = window.setInterval(() => {
      index = (index + 1) % LOADING_TIPS.length;
      setText(tip, LOADING_TIPS[index]);
    }, 2400);
  }

  function stopTipRotation(state) {
    if (!state.tipTimer) return;
    window.clearInterval(state.tipTimer);
    state.tipTimer = null;
  }

  function setAutoRotate(root, state, enabled) {
    const viewer = root.querySelector('[data-lumira-model-viewer]');
    const button = root.querySelector('[data-lumira-3d-auto]');
    state.autoRotate = Boolean(enabled);
    if (button) {
      button.setAttribute('aria-pressed', state.autoRotate ? 'true' : 'false');
      button.classList.toggle('is-active', state.autoRotate);
      button.textContent = state.autoRotate ? 'Rotating' : 'Auto rotate';
    }
    if (!viewer) return;
    if (state.autoRotate) {
      if (state.cameraFit) {
        const current = currentCameraParts(viewer, state.cameraFit);
        applyCameraLimits(viewer, state.cameraFit);
        setViewerDistance(viewer, state.cameraFit, state.cameraFit.distance, current.azimuth, current.polar);
      }
      viewer.setAttribute('rotation-per-second', '24deg');
      viewer.setAttribute('auto-rotate-delay', '0');
      viewer.setAttribute('auto-rotate', '');
    }
    else viewer.removeAttribute('auto-rotate');
  }

  function startAutoPreview(root, state) {
    if (state.didAutoPreview || state.autoPreviewCancelled) return;
    state.didAutoPreview = true;
    setAutoRotate(root, state, true);
    state.autoPreviewTimer = window.setTimeout(() => {
      setAutoRotate(root, state, false);
      root.classList.add('has-auto-previewed');
      state.autoPreviewTimer = null;
    }, AUTO_PREVIEW_MS);
  }

  function cancelAutoPreview(state) {
    state.autoPreviewCancelled = true;
    if (!state.autoPreviewTimer) return;
    window.clearTimeout(state.autoPreviewTimer);
    state.autoPreviewTimer = null;
  }

  function openModal(root) {
    const modal = root.querySelector('[data-lumira-3d-modal]');
    if (!modal) return;
    root.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('lumira3d-lock');
    setTimeout(() => {
      const close = root.querySelector('[data-lumira-3d-close]');
      if (close) close.focus({ preventScroll: true });
    }, 40);
  }

  function closeModal(root) {
    const modal = root.querySelector('[data-lumira-3d-modal]');
    if (!modal) return;
    root.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('lumira3d-lock');
  }

  function setArFeedback(root, message, active) {
    const button = root.querySelector('[data-lumira-3d-ar]');
    const status = root.querySelector('[data-lumira-3d-ar-status]');
    root.classList.toggle('is-ar-launching', Boolean(active));
    if (button) {
      if (!button.dataset.readyLabel) button.dataset.readyLabel = button.textContent;
      button.textContent = active ? 'Launching AR' : button.dataset.readyLabel;
      button.disabled = Boolean(active);
    }
    setText(status, message || '');
  }

  function showArFallback(root, message) {
    const fallback = root.querySelector('[data-lumira-3d-ar-fallback]');
    if (fallback) {
      fallback.hidden = false;
      fallback.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
    setArFeedback(root, message || 'Scan the QR code to continue on your phone.', false);
  }

  function buildButton(label, value, className) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = className;
    button.dataset.value = value;
    button.setAttribute('aria-pressed', 'false');
    button.textContent = label;
    return button;
  }

  function renderControls(root, config, state) {
    const colorSlot = root.querySelector('[data-lumira-3d-colors]');
    const pieceSlot = root.querySelector('[data-lumira-3d-pieces]');

    if (colorSlot && !colorSlot.dataset.ready) {
      const colorOptions = Engine.collectColorOptions(config);
      colorOptions.forEach((color) => {
        const button = buildButton(color.name, color.name, 'lumira3d-swatch');
        button.style.setProperty('--swatch-color', color.hex);
        button.dataset.hex = color.hex;
        button.title = color.name;
        colorSlot.appendChild(button);
      });
      const colorSection = colorSlot.closest('.lumira3d-section');
      if (colorSection) {
        colorSection.hidden = colorOptions.length === 0;
        if (!colorOptions.length) colorSection.style.display = 'none';
      }
      colorSlot.dataset.ready = 'true';
    }

    if (pieceSlot && !pieceSlot.dataset.ready) {
      const pieceOptions = Engine.collectPieceOptions(config);
      if (!state.pieces && pieceOptions.length === 1) state.pieces = pieceOptions[0];
      pieceOptions.forEach((count) => {
        pieceSlot.appendChild(buildButton(count.toUpperCase(), count, 'lumira3d-segment'));
      });
      const pieceSection = pieceSlot.closest('.lumira3d-section');
      if (pieceSection) {
        pieceSection.hidden = pieceOptions.length === 0;
        if (!pieceOptions.length) pieceSection.style.display = 'none';
      }
      pieceSlot.dataset.ready = 'true';
    }

    setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-colors] button')), state.color);
    setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-pieces] button')), state.pieces);
  }

  function updateVariant(root, config, state) {
    const variant = Engine.findVariant(config, state);
    state.variant = variant;

    const price = root.querySelector('[data-lumira-3d-price]');
    const add = root.querySelector('[data-lumira-3d-add]');
    if (price) price.textContent = moneyLabel(config, variant);
    if (add) {
      add.disabled = !variant || variant.available === false;
      add.dataset.variantId = variant && variant.id ? String(variant.id) : '';
    }

    root.dispatchEvent(new CustomEvent('lumira3d:variant-change', {
      bubbles: true,
      detail: { variant, state: Object.assign({}, state) }
    }));
  }

  function setupViewer(root, config, state) {
    const viewer = root.querySelector('[data-lumira-model-viewer]');
    if (!viewer) return;

    root.classList.add('is-loading');
    updateLoading(root, 0);
    startTipRotation(root, state);

    if (config.product.poster) viewer.setAttribute('poster', config.product.poster);
    viewer.setAttribute('ar-scale', 'fixed');
    viewer.setAttribute('ar-placement', 'floor');
    viewer.setAttribute('interaction-prompt', 'auto');
    Engine.applyLighting(viewer, state.lighting);

    viewer.addEventListener('progress', (event) => {
      const progress = event.detail && typeof event.detail.totalProgress === 'number'
        ? event.detail.totalProgress
        : 0;
      updateLoading(root, progress);
    });

    viewer.addEventListener('load', () => {
      if (state.colorHex) Engine.applyModelColor(viewer, state.colorHex);
      root.classList.add('is-loaded');
      root.classList.remove('is-loading');
      updateLoading(root, 1);
      stopTipRotation(state);
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => fitViewerCamera(viewer, state));
      });
    });

    viewer.addEventListener('error', () => {
      root.classList.add('has-error');
      root.classList.remove('is-loading');
      stopTipRotation(state);
    });

    let modelStarted = false;
    let preloadTimer = null;
    const preloader = document.createElement('model-viewer');
    preloader.setAttribute('aria-hidden', 'true');
    preloader.setAttribute('loading', 'eager');
    preloader.setAttribute('reveal', 'auto');
    preloader.style.cssText = 'position:fixed;top:0;left:0;width:2px;height:2px;opacity:0;pointer-events:none;z-index:-1';

    function startMeasuredModel(factor) {
      if (modelStarted) return;
      modelStarted = true;
      if (preloadTimer) window.clearTimeout(preloadTimer);
      preloader.remove();
      const safeFactor = Number.isFinite(factor) && factor > 0 ? factor : 1;
      const scale = safeFactor.toFixed(4);
      viewer.setAttribute('scale', `${scale} ${scale} ${scale}`);
      viewer.dataset.lumiraPhysicalScale = scale;
      viewer.setAttribute('src', config.model.src);
    }

    preloader.addEventListener('progress', (event) => {
      const progress = event.detail && typeof event.detail.totalProgress === 'number'
        ? event.detail.totalProgress
        : 0;
      updateLoading(root, progress * 0.45);
    });
    preloader.addEventListener('load', () => {
      startMeasuredModel(measurePhysicalScale(preloader, config));
    }, { once: true });
    preloader.addEventListener('error', () => {
      startMeasuredModel(1);
    }, { once: true });
    document.body.appendChild(preloader);
    preloader.setAttribute('src', config.model.src);
    preloadTimer = window.setTimeout(() => startMeasuredModel(1), 15000);

    Engine.renderQrFallback(root, config);
  }

  function directModelUrl(url) {
    const pathname = url.pathname.toLowerCase();
    return pathname.endsWith('.glb') || pathname.endsWith('.gltf');
  }

  function compareUrl(value) {
    const trimmed = String(value || '').trim();
    if (!trimmed) return null;
    const url = new URL(trimmed, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error('Unsupported compare URL');
    }
    return url;
  }

  function setCompareStatus(root, message, mode) {
    const status = root.querySelector('[data-lumira-3d-compare-status]');
    setText(status, message);
    if (status) {
      status.dataset.mode = mode || 'idle';
    }
  }

  function clearCompare(root, state) {
    const pane = root.querySelector('[data-lumira-3d-compare-pane]');
    const viewer = root.querySelector('[data-lumira-compare-viewer]');
    root.classList.remove('is-compare-active');
    if (pane) pane.hidden = true;
    if (viewer) {
      viewer.removeAttribute('src');
      viewer.removeAttribute('ios-src');
    }
    state.compareActive = false;
    setCompareStatus(root, 'Ready when another 3D product is available.', 'idle');
  }

  function activateCompare(root, state, modelSrc, title, iosSrc) {
    const pane = root.querySelector('[data-lumira-3d-compare-pane]');
    let viewer = root.querySelector('[data-lumira-compare-viewer]');
    const titleNode = root.querySelector('[data-lumira-3d-compare-title]');
    if (!pane || !viewer || !modelSrc) throw new Error('Comparison viewer is unavailable');

    if (viewer.tagName !== 'MODEL-VIEWER') {
      const replacement = document.createElement('model-viewer');
      replacement.setAttribute('data-lumira-compare-viewer', '');
      replacement.setAttribute('camera-controls', '');
      replacement.setAttribute('interaction-prompt', 'none');
      replacement.setAttribute('loading', 'lazy');
      replacement.setAttribute('reveal', 'auto');
      replacement.setAttribute('shadow-softness', '0.84');
      replacement.setAttribute('camera-orbit', '20deg 68deg auto');
      replacement.setAttribute('min-camera-orbit', 'auto 34deg auto');
      replacement.setAttribute('max-camera-orbit', 'auto 86deg auto');
      viewer.replaceWith(replacement);
      viewer = replacement;
    }

    pane.hidden = false;
    root.classList.add('is-compare-active');
    setText(titleNode, title || 'Comparison');
    viewer.src = modelSrc;
    if (iosSrc) viewer.setAttribute('ios-src', iosSrc);
    else viewer.removeAttribute('ios-src');
    Engine.applyLighting(viewer, state.lighting);
    state.compareActive = true;
    setCompareStatus(root, title ? `Comparing ${title}` : 'Comparison model loaded.', 'success');
  }

  async function loadCompare(root, config, state) {
    const input = root.querySelector('[data-lumira-3d-compare-input]');
    const rawValue = input && input.value;
    let url;
    try {
      url = compareUrl(rawValue);
    } catch (error) {
      setCompareStatus(root, 'Enter a valid product URL or direct GLB link.', 'error');
      return;
    }

    if (!url) {
      setCompareStatus(root, 'Enter a product URL or direct GLB link.', 'idle');
      return;
    }

    setCompareStatus(root, 'Loading comparison...', 'loading');

    try {
      if (directModelUrl(url)) {
        activateCompare(root, state, url.href, 'Comparison model');
        return;
      }

      if (url.origin !== window.location.origin) {
        throw new Error('Product URL must be on this store');
      }

      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product page did not load');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const configNode = doc.querySelector('[data-lumira-3d-config]');
      if (!configNode) throw new Error('No 3D model config found');

      const compareConfig = JSON.parse(configNode.textContent || '{}');
      const src = compareConfig && compareConfig.model && compareConfig.model.src;
      if (!src) throw new Error('No GLB source found');

      const title = compareConfig.product && compareConfig.product.title;
      const iosSrc = compareConfig.model && compareConfig.model.iosSrc;
      activateCompare(root, state, new URL(src, url.href).href, title, iosSrc ? new URL(iosSrc, url.href).href : '');
    } catch (error) {
      console.warn('[Lumira 3D] Compare load failed', error);
      clearCompare(root, state);
      setCompareStatus(root, 'Comparison is unavailable for that product.', 'error');
    }
  }

  async function addToCart(button) {
    const variantId = button.dataset.variantId;
    if (!variantId) throw new Error('Missing variant ID');

    button.disabled = true;
    button.classList.add('is-loading');
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity: 1 })
      });
      if (!response.ok) throw new Error('Cart add failed');
      window.location.href = '/cart';
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;'
    }[char]));
  }

  function initInYourSpace(root, productHandle) {
    if (!root || root.dataset.lumiraSpaceReady === 'true') return;
    root.dataset.lumiraSpaceReady = 'true';

    const photoInput = root.querySelector('[data-lumira-space-photo]');
    const floorplanInput = root.querySelector('[data-lumira-space-floorplan]');
    const describeInput = root.querySelector('[data-lumira-space-describe]');
    const describeGo = root.querySelector('[data-lumira-space-describe-go]');
    const resultDiv = root.querySelector('[data-lumira-space-result]');

    if (!resultDiv) return;

    async function postWithFallback(endpoint, options) {
      return fetch(endpoint, options).catch(() => fetch(`https://designer.lumira.com${endpoint}`, options));
    }

    async function handleUpload(file, type) {
      resultDiv.hidden = false;
      resultDiv.innerHTML = '<span class="lumira3d-space-loading">Scanning your space...</span>';
      const formData = new FormData();
      formData.append(type === 'photo' ? 'photo' : 'floorplan', file);
      formData.append('product_handle', productHandle);

      try {
        const endpoint = type === 'photo' ? '/api/analyze-room' : '/api/analyze-floorplan';
        const response = await postWithFallback(endpoint, { method: 'POST', body: formData });
        if (!response.ok) throw new Error('Room analysis failed');
        renderMatch(await response.json());
      } catch (error) {
        resultDiv.innerHTML = '<span class="lumira3d-space-error">Could not analyze image. Try describing your room instead.</span>';
      }
    }

    async function handleDescribe() {
      const description = describeInput ? describeInput.value.trim() : '';
      if (!description) return;
      resultDiv.hidden = false;
      resultDiv.innerHTML = '<span class="lumira3d-space-loading">Finding a matching room...</span>';

      try {
        const response = await postWithFallback('/api/match-room', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description, product_handle: productHandle })
        });
        if (!response.ok) throw new Error('Room match failed');
        renderMatch(await response.json());
      } catch (error) {
        resultDiv.innerHTML = '<span class="lumira3d-space-error">Could not match your room. Try uploading a photo.</span>';
      }
    }

    function renderMatch(result) {
      const templateId = result && (result.template_id || result.best_template_id);
      if (!templateId) {
        resultDiv.innerHTML = '<span class="lumira3d-space-error">No matching room template found.</span>';
        return;
      }

      const name = result.name || result.room_name || templateId;
      const reasoning = result.reasoning || result.reason || result.sophi_message || '';
      const designerUrl = `http://72.62.161.109:3100/en/designer/?product=${encodeURIComponent(productHandle)}&room=${encodeURIComponent(templateId)}`;
      resultDiv.innerHTML = `
        <div class="lumira3d-space-match">
          <span class="lumira3d-space-match-label">Best match:</span>
          <strong>${escapeHTML(name)}</strong>
          ${reasoning ? `<small>${escapeHTML(reasoning)}</small>` : ''}
          <a href="${designerUrl}" class="lumira3d-space-match-cta">See this piece in the room &rarr;</a>
        </div>
      `;
    }

    if (photoInput) {
      photoInput.addEventListener('change', (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) handleUpload(file, 'photo');
      });
    }
    if (floorplanInput) {
      floorplanInput.addEventListener('change', (event) => {
        const file = event.target.files && event.target.files[0];
        if (file) handleUpload(file, 'floorplan');
      });
    }
    if (describeGo) describeGo.addEventListener('click', handleDescribe);
    if (describeInput) {
      describeInput.addEventListener('keypress', (event) => {
        if (event.key === 'Enter') handleDescribe();
      });
    }
  }

  function init(root) {
    if (root.dataset.lumiraReady === 'true') return;
    const config = Engine.parseConfig(root);
    if (!config) {
      root.hidden = true;
      return;
    }

    root.dataset.lumiraReady = 'true';
    const state = {
      color: '',
      colorHex: '',
      pieces: '',
      lighting: 'studio',
      background: 'white',
      autoRotate: false,
      variant: null,
      tipTimer: null,
      autoPreviewTimer: null,
      autoPreviewCancelled: false,
      didAutoPreview: false,
      compareActive: false,
      wishlist: false,
      cameraFit: null,
      viewerInitialized: false,
      viewerInitializing: false,
      zoomHoldTimer: null,
      zoomHoldInterval: null,
      zoomHoldTicks: 0,
      zoomSuppressClick: false
    };

    renderControls(root, config, state);
    updateVariant(root, config, state);
    renderFitGuidance(root, config, null);
    requestFitGuidance(root, config);
    initInYourSpace(root, config.product && config.product.handle);

    function ensureViewer() {
      if (state.viewerInitialized || state.viewerInitializing) return;
      state.viewerInitializing = true;
      let modelViewerReady;
      try {
        modelViewerReady = Engine.loadModelViewer();
      } catch (error) {
        state.viewerInitializing = false;
        console.warn('[Lumira 3D] model-viewer did not load', error);
        root.classList.add('has-error');
        return;
      }
      Promise.resolve(modelViewerReady).then(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            setupViewer(root, config, state);
            state.viewerInitialized = true;
            state.viewerInitializing = false;
          });
        });
      }).catch((error) => {
        state.viewerInitializing = false;
        console.warn('[Lumira 3D] model-viewer did not load', error);
        root.classList.add('has-error');
      });
    }

    function openViewer(trigger) {
      if (trigger && root.contains(trigger) && root.parentElement !== document.body) {
        const originalParent = root.parentNode;
        originalParent.insertBefore(trigger, root);
        trigger.addEventListener('click', () => openViewer(trigger));
        document.body.appendChild(root);
      }
      openModal(root);
      ensureViewer();
    }

    root.addEventListener('click', (event) => {
      const open = event.target.closest('[data-lumira-3d-open]');
      if (open) {
        openViewer(open);
        return;
      }

      const close = event.target.closest('[data-lumira-3d-close]');
      if (close) {
        closeModal(root);
        return;
      }

      const color = event.target.closest('[data-lumira-3d-colors] button');
      if (color) {
        state.color = color.dataset.value || '';
        state.colorHex = color.dataset.hex || Engine.inferHexColor(state.color);
        setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-colors] button')), state.color);
        Engine.applyModelColor(root.querySelector('[data-lumira-model-viewer]'), state.colorHex);
        updateVariant(root, config, state);
        return;
      }

      const piece = event.target.closest('[data-lumira-3d-pieces] button');
      if (piece) {
        state.pieces = piece.dataset.value || '';
        setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-pieces] button')), state.pieces);
        updateVariant(root, config, state);
        return;
      }

      const lighting = event.target.closest('[data-lumira-3d-lighting] button');
      if (lighting) {
        state.lighting = lighting.dataset.value || 'studio';
        setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-lighting] button')), state.lighting);
        root.dataset.lumiraLighting = state.lighting;
        Engine.applyLighting(root.querySelector('[data-lumira-model-viewer]'), state.lighting);
        Engine.applyLighting(root.querySelector('[data-lumira-compare-viewer]'), state.lighting);
        return;
      }

      const background = event.target.closest('[data-lumira-3d-background] button');
      if (background) {
        state.background = background.dataset.value || 'white';
        setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-background] button')), state.background);
        Engine.setBackground(root, state.background);
        if (state.background === 'room') {
          const upload = root.querySelector('[data-lumira-3d-room]');
          if (upload && !root.style.getPropertyValue('--lumira-room-image')) upload.click();
        }
        return;
      }

      const ar = event.target.closest('[data-lumira-3d-ar]');
      if (ar) {
        const viewer = root.querySelector('[data-lumira-model-viewer]');
        if (viewer && typeof viewer.activateAR === 'function' && Engine.isMobileARDevice()) {
          setArFeedback(root, 'Launching AR view...', true);
          Promise.resolve(viewer.activateAR())
            .then(() => {
              window.setTimeout(() => {
                setArFeedback(root, 'AR view opened on this device.', false);
              }, 900);
            })
            .catch(() => {
              showArFallback(root, 'AR did not launch. Scan the QR code to continue on your phone.');
            });
        } else {
          showArFallback(root, 'Scan the QR code to continue on your phone.');
        }
        return;
      }

      const auto = event.target.closest('[data-lumira-3d-auto]');
      if (auto) {
        cancelAutoPreview(state);
        setAutoRotate(root, state, !state.autoRotate);
        return;
      }

      const reset = event.target.closest('[data-lumira-3d-reset]');
      if (reset) {
        resetViewerCamera(root, state);
        return;
      }

      const zoom = event.target.closest('[data-lumira-3d-zoom]');
      if (zoom) {
        if (state.zoomSuppressClick) {
          state.zoomSuppressClick = false;
          return;
        }
        zoomViewer(root, state, zoom.getAttribute('data-lumira-3d-zoom'), 1);
        return;
      }

      const viewport = event.target.closest('[data-lumira-model-viewer]');
      if (viewport && state.autoRotate) {
        cancelAutoPreview(state);
        setAutoRotate(root, state, false);
        return;
      }

      const add = event.target.closest('[data-lumira-3d-add]');
      if (add) {
        addToCart(add).catch(() => {
          add.textContent = 'Try again';
          setTimeout(() => { add.textContent = 'Add to Cart'; }, 1600);
        });
        return;
      }

      const wishlist = event.target.closest('[data-lumira-3d-wishlist]');
      if (wishlist) {
        state.wishlist = !state.wishlist;
        wishlist.setAttribute('aria-pressed', state.wishlist ? 'true' : 'false');
        wishlist.classList.toggle('is-active', state.wishlist);
        wishlist.textContent = state.wishlist ? 'Saved' : 'Save';
        setText(
          root.querySelector('[data-lumira-3d-wishlist-status]'),
          state.wishlist ? 'Saved for this session.' : ''
        );
        return;
      }

      const compareLoad = event.target.closest('[data-lumira-3d-compare-load]');
      if (compareLoad) {
        loadCompare(root, config, state);
        return;
      }

      const compareClose = event.target.closest('[data-lumira-3d-compare-close]');
      if (compareClose) {
        clearCompare(root, state);
      }
    });

    root.addEventListener('submit', (event) => {
      if (!event.target.closest('[data-lumira-3d-root]')) return;
      event.preventDefault();
    });

    function stopZoomHold() {
      if (state.zoomHoldTimer) window.clearTimeout(state.zoomHoldTimer);
      if (state.zoomHoldInterval) window.clearInterval(state.zoomHoldInterval);
      state.zoomHoldTimer = null;
      state.zoomHoldInterval = null;
      state.zoomHoldTicks = 0;
    }

    root.addEventListener('pointerdown', (event) => {
      const zoom = event.target.closest('[data-lumira-3d-zoom]');
      if (!zoom) return;
      event.preventDefault();
      stopZoomHold();
      const direction = zoom.getAttribute('data-lumira-3d-zoom');
      state.zoomSuppressClick = true;
      zoomViewer(root, state, direction, 1);
      state.zoomHoldTimer = window.setTimeout(() => {
        state.zoomHoldInterval = window.setInterval(() => {
          state.zoomHoldTicks += 1;
          zoomViewer(root, state, direction, Math.min(2.2, 1 + state.zoomHoldTicks * 0.12));
        }, 110);
      }, 320);
    });

    ['pointerup', 'pointercancel', 'pointerleave'].forEach((eventName) => {
      root.addEventListener(eventName, stopZoomHold);
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.closest('[data-lumira-3d-compare-input]')) return;
      event.preventDefault();
      loadCompare(root, config, state);
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-lumira-3d-room]');
      if (!input || !input.files || !input.files[0]) return;
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        Engine.setBackground(root, 'room', reader.result);
      }, { once: true });
      reader.readAsDataURL(input.files[0]);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && root.classList.contains('is-open')) closeModal(root);
    });

    setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-lighting] button')), state.lighting);
    setPressed(Array.from(root.querySelectorAll('[data-lumira-3d-background] button')), state.background);
    setAutoRotate(root, state, false);
  }

  function ready() {
    document.querySelectorAll('[data-lumira-3d-root]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ready, { once: true });
  } else {
    ready();
  }
})();
